import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import type { OnboardingStep } from '../../data/onboarding';
type OnboardingTourProps = {
  steps: OnboardingStep[];
  index: number;
  onBack: () => void;
  onNext: () => void;
  onClose: () => void;
};
type Placement = {
  left: number;
  top: number;
  arrowTop: number;
  side: 'right' | 'left';
};
const WIDTH = 360;
const GAP = 14;
const ease = [0.23, 1, 0.32, 1] as const;
function findTarget(step: OnboardingStep): DOMRect | null {
  const els = document.querySelectorAll<HTMLElement>(step.target);
  const el = els[Math.min(step.index ?? 0, els.length - 1)];
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  if (!step.center) return r;
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  return new DOMRect(cx - 40, cy - 40, 80, 80);
}

/** Step-by-step coach marks that point at the new features. */
export function OnboardingTour({
  steps,
  index,
  onBack,
  onNext,
  onClose
}: OnboardingTourProps) {
  const step = steps[index];
  const cardRef = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<Placement | null>(null);
  const last = index === steps.length - 1;

  // Track the target every frame: panels animate open and the layout shifts between steps.
  useLayoutEffect(() => {
    let frame = 0;
    const measure = () => {
      frame = requestAnimationFrame(measure);
      const r = findTarget(step);
      const card = cardRef.current;
      if (!r || !card) {
        setPlace(p => p === null ? p : null);
        return;
      }
      const h = card.offsetHeight;
      const side: Placement['side'] = r.right + GAP + WIDTH <= window.innerWidth - 8 ? 'right' : 'left';
      const left = side === 'right' ? r.right + GAP : r.left - GAP - WIDTH;
      const mid = r.top + r.height / 2;
      const top = Math.min(Math.max(mid - h / 2, 34), window.innerHeight - h - 8);
      const arrowTop = Math.min(Math.max(mid - top, 22), h - 22);
      setPlace(p => p && Math.abs(p.left - left) < 0.5 && Math.abs(p.top - top) < 0.5 && Math.abs(p.arrowTop - arrowTop) < 0.5 && p.side === side ? p : {
        left,
        top,
        arrowTop,
        side
      });
    };
    measure();
    return () => cancelAnimationFrame(frame);
  }, [step]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return <motion.div ref={cardRef} role="dialog" aria-modal="false" aria-labelledby="onboarding-title" initial={{
    opacity: 0,
    scale: 0.96
  }} animate={{
    opacity: place ? 1 : 0,
    scale: 1,
    left: place?.left ?? -9999,
    top: place?.top ?? 0
  }} exit={{
    opacity: 0,
    scale: 0.96
  }} transition={{
    duration: 0.22,
    ease
  }} style={{
    width: WIDTH
  }} className="fixed z-[55] rounded-[12px] bg-white px-[20px] pb-[16px] pt-[16px] shadow-[0_10px_32px_rgba(20,28,38,0.22)] ring-1 ring-black/[0.06]" data-id="element-326" data-mp="3bedb9spz0otw">
      
      {place && <span aria-hidden="true" style={{
      top: place.arrowTop - 9
    }} className={`absolute h-[18px] w-[18px] rotate-45 bg-white ${place.side === 'right' ? '-left-[8px] shadow-[-2px_2px_3px_rgba(20,28,38,0.06)]' : '-right-[8px] shadow-[2px_-2px_3px_rgba(20,28,38,0.06)]'}`} data-id="element-327" data-mp="1sd1h4ri5c0ss" />}
      <div className="relative flex items-start justify-between gap-4" data-id="element-328" data-mp="1eh7jx5qqizvi">
        <h2 id="onboarding-title" className="text-[17px] font-extrabold uppercase leading-tight tracking-[0.01em] text-pf-blue" data-id="element-329" data-mp="19huqg25cyujy">
          {step.title}
        </h2>
        <button type="button" onClick={onClose} aria-label="Close onboarding" className="-mr-[4px] -mt-[2px] flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-pf-ink transition-colors duration-150 hover:bg-pf-panel" data-id="element-330" data-mp="1je7qdhrhy4n5">
          
          <XIcon className="h-[18px] w-[18px]" strokeWidth={2.8} data-id="element-331" data-mp="3q4ktngcozo0x" />
        </button>
      </div>
      <motion.p key={step.id} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.18,
      ease
    }} className="relative mt-[8px] text-[13.5px] leading-[1.55] text-[#3d4248]" data-id="element-332" data-mp="3u4uy2oku66do">
        
        {step.description}
      </motion.p>
      <div className="relative mt-[16px] grid grid-cols-[1fr_auto_1fr] items-center" data-id="element-333" data-mp="cxj0lwlqib2h">
        <div data-id="element-334" data-mp="gyuw2irahqil">
          {index > 0 && <button type="button" onClick={onBack} className="h-[32px] rounded-full border border-pf-blue-line px-[16px] text-[13px] font-medium text-pf-blue transition-colors duration-150 hover:bg-pf-blue-soft" data-id="element-335" data-mp="gxt7rcl9j7ix">
            
              Back
            </button>}
        </div>
        <span className="text-[13px] tabular-nums text-pf-muted" data-id="element-336" data-mp="vpbsfnp2hq8m">
          {index + 1} / {steps.length}
        </span>
        <div className="flex justify-end" data-id="element-337" data-mp="10kr3k7vox81c">
          <button type="button" onClick={last ? onClose : onNext} autoFocus className="h-[32px] rounded-full bg-pf-blue px-[18px] text-[13px] font-semibold text-white transition-[background-color,transform] duration-150 hover:bg-[#0a57b3] active:scale-[0.97]" data-id="element-338" data-mp="2l561xekjbjim">
            
            {last ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </motion.div>;
}